import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';
import backpack from '~/assets/icons/backpack_2.svg';
import cross from '~/assets/icons/cross.svg';
import { playSound, playSoundLoop, Sound } from '~/assets/sounds';
import { Divider } from '~/components/ui/Divider';
import { PanelControls } from '~/components/ui/PanelControls';
import { SearchInput } from '~/components/ui/SearchInput';
import { ShopContainer } from '~/components/ui/Shop';
import { ShopBalance } from '~/components/ui/ShopBalance';
import { ShopHead } from '~/components/ui/ShopHead';
import { ShopPreview } from '~/components/ui/ShopPreview';
import { Text } from '~/components/ui/Text';
import { rage, useInputAction, useObservable } from '~/rage';
import { InputAction, InputKey, WalletType } from '~/shared';
import { CategoryTab } from '../../components/CategoryTab';
import { ListTab } from '../../components/ListTab';
import { useBuy, useShopPreview } from '../../hooks';
import {
	resetStore,
	setActiveStage,
	setPlayerGender,
	setSearchValue,
	setShopItems,
	StageTypes,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { ShopLogo, ShopLogoHeight, ShopTypes } from './ClothesShop.model';
import {
	StyledCloseButtonContainer,
	StyledControlsContainer,
	StyledInventoryInfoContainer,
	StyledRightContainer,
	StyledSearchContainer,
} from './ClothesShop.styled';

const ClothesShop: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const activeStage = useLayoutSelector(
		(state) => state.clothesShop.activeStage,
	);
	const [activeVariants, setActiveVariants] = useState<Record<string, number>>(
		{},
	);
	const shopItems = useObservable(rage.clothesShop.getShopItems());
	const playerGender = useObservable(rage.player.getPlayerGender());
	const modalState = useObservable(rage.modal.getCurrentModal());
	const { handleBuy } = useBuy(activeVariants);
	const handleVariantChange = useCallback(
		(itemId: string, newIndex: number) => {
			setActiveVariants((prev) => ({ ...prev, [itemId]: newIndex }));
		},
		[],
	);
	const stopSound = useMemo(() => playSoundLoop(Sound.ClothesShopMusic), []);
	const shopPreview = useShopPreview(activeVariants, handleVariantChange);
	const searchValue = useLayoutSelector(
		(state) => state.clothesShop.searchValue,
	);
	const inventorySlotsCount = useObservable(
		rage.inventory.getInventorySlotsCount(),
	);

	const resetVariants = useCallback(() => {
		setActiveVariants({});
	}, []);

	const leftControls = useMemo(() => {
		if (activeStage === StageTypes.Category) {
			return [
				{
					text: 'Управление',
					keys: [
						InputKey.ArrowUp,
						InputKey.ArrowDown,
						InputKey.ArrowLeft,
						InputKey.ArrowRight,
					],
				},
				{
					text: 'Выбрать категорию',
					keys: [InputKey.Enter],
					onClick: () => dispatch(setActiveStage(StageTypes.List)),
				},
				{
					text: 'Крутить камеру',
					keys: [InputKey.MouseRight],
				},
			];
		}

		if (activeStage === StageTypes.List) {
			return [
				{
					text: 'Управление',
					keys: [
						InputKey.ArrowUp,
						InputKey.ArrowDown,
						InputKey.ArrowLeft,
						InputKey.ArrowRight,
					],
				},
				{
					text: 'Купить',
					keys: [InputKey.Enter],
					onClick: () => handleBuy(WalletType.Cash),
				},
				{
					text: 'Крутить камеру',
					keys: [InputKey.MouseRight],
				},
			];
		}

		return [];
	}, [activeStage, handleBuy]);

	const rightControls = [
		{
			text: 'Выйти',
			keys: ['Esc'],
			onClick: () =>
				rage.clothesShop
					.exitShop()
					.catch((err) => rage.notifications.showException(err)),
		},
	];

	const handleSearch = useCallback((value: string) => {
		dispatch(setSearchValue(value));
		if (value.trim().length > 0) {
			dispatch(setActiveStage(StageTypes.List));
		}
	}, []);

	useInputAction(InputAction.CommonMenuClose, () => {
		if (modalState) {
			return;
		}

		if (activeStage === StageTypes.List) {
			dispatch(setActiveStage(StageTypes.Category));
			return;
		}

		rage.clothesShop
			.exitShop()
			.catch((err) => rage.notifications.showException(err));
	});

	useEffect(() => {
		dispatch(setShopItems(shopItems));
	}, [shopItems]);

	useEffect(() => {
		dispatch(setPlayerGender(playerGender));
	}, [playerGender]);

	useEffectOnce(() => {
		return () => {
			dispatch(resetStore());
			stopSound();
		};
	});

	return (
		<Fragment>
			<StyledRightContainer>
				<StyledInventoryInfoContainer>
					<img src={backpack} />
					<Divider $direction="V" style={{ opacity: 0.1 }} />
					<Text>
						{inventorySlotsCount.available.toLocaleString('ru-RU')}
						<span>
							&nbsp;/&nbsp;{inventorySlotsCount.total.toLocaleString('ru-RU')}
						</span>
					</Text>
				</StyledInventoryInfoContainer>
				<ShopBalance />
				<StyledCloseButtonContainer
					onClick={() => {
						rage.clothesShop
							.exitShop()
							.catch((err) => rage.notifications.showException(err));
						playSound(Sound.ClickButton);
					}}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					<img src={cross} width={20} height={20} />
				</StyledCloseButtonContainer>
			</StyledRightContainer>
			<ShopContainer>
				<ShopHead
					img={ShopLogo[ShopTypes.MaFashion]}
					text={''}
					height={ShopLogoHeight[ShopTypes.MaFashion]}
				/>
				<StyledSearchContainer>
					<SearchInput
						placeholder="Введите название для поиска"
						value={searchValue}
						onChange={(e) => handleSearch(e.target.value)}
						active={searchValue.length > 0}
					/>
				</StyledSearchContainer>
				{activeStage === StageTypes.List && (
					<ListTab
						activeVariants={activeVariants}
						onVariantChange={handleVariantChange}
						resetVariants={resetVariants}
					/>
				)}
				{activeStage === StageTypes.Category && <CategoryTab />}
			</ShopContainer>
			<StyledControlsContainer>
				<PanelControls controls={leftControls} align="left" />
				<PanelControls controls={rightControls} align="right" />
			</StyledControlsContainer>
			<ShopPreview {...shopPreview} />
		</Fragment>
	);
};

export { ClothesShop };
